import { useCallback, useState } from "react";
import { uploadMediaFile, validateMediaFile } from "./Uploadmedia.js";

/*
 * mode:
 * - "single"   -> only one file
 * - "multiple" -> multiple files
 */
export function useMultiMediaUpload({
  allowedTypes,
  allowedTypePrefix,
  maxSizeMB,
  maxCount,
  mode = "single",
} = {}) {
  const isMultiple = mode === "multiple";

  const [items, setItems] = useState([]); // [{ file, preview, url, uploading }]
  const [error, setError] = useState("");

  const reset = useCallback(() => {
    setItems((prev) => {
      prev.forEach((item) => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });

      return [];
    });

    setError("");
  }, []);

  const removeAt = useCallback((preview) => {
    setItems((prev) => {
      const target = prev.find((i) => i.preview === preview);

      if (target?.preview) {
        URL.revokeObjectURL(target.preview);
      }

      return prev.filter((i) => i.preview !== preview);
    });
  }, []);

  const addFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);

      if (!files.length) return;

      setItems((current) => {
        // SINGLE MODE

        if (!isMultiple) {
          const file = files[0];

          const validationError = validateMediaFile(file, {
            allowedTypes,
            allowedTypePrefix,
            maxSizeMB,
          });

          if (validationError) {
            setError(validationError);
            return current;
          }

          // Remove previous file
          current.forEach((item) => {
            if (item.preview) {
              URL.revokeObjectURL(item.preview);
            }
          });

          const newItem = {
            file,
            preview: URL.createObjectURL(file),
            url: "",
            uploading: true,
          };

          uploadMediaFile(file)
            .then((uploadedUrl) => {
              setItems((prev) =>
                prev.map((item) =>
                  item.preview === newItem.preview
                    ? {
                        ...item,
                        url: uploadedUrl,
                        uploading: false,
                      }
                    : item,
                ),
              );
            })
            .catch((err) => {
              console.error("Media upload error:", err);

              setError("Error uploading file. Please try again.");

              setItems((prev) =>
                prev.filter((item) => item.preview !== newItem.preview),
              );

              URL.revokeObjectURL(newItem.preview);
            });

          setError("");

          return [newItem];
        }

        // MULTIPLE MODE

        const remaining = maxCount ? maxCount - current.length : files.length;

        if (remaining <= 0) {
          setError(`You can attach up to ${maxCount} files.`);
          return current;
        }

        const accepted = [];
        let validationError = "";

        for (const f of files.slice(0, remaining)) {
          const err = validateMediaFile(f, {
            allowedTypes,
            allowedTypePrefix,
            maxSizeMB,
          });

          if (err) {
            validationError = err;
            continue;
          }

          accepted.push(f);
        }

        setError(validationError);

        if (!accepted.length) return current;

        const newItems = accepted.map((file) => ({
          file,
          preview: URL.createObjectURL(file),
          url: "",
          uploading: true,
        }));

        newItems.forEach((item) => {
          uploadMediaFile(item.file)
            .then((uploadedUrl) => {
              setItems((prev) =>
                prev.map((it) =>
                  it.preview === item.preview
                    ? {
                        ...it,
                        url: uploadedUrl,
                        uploading: false,
                      }
                    : it,
                ),
              );
            })
            .catch((err) => {
              console.error("Media upload error:", err);

              setError("Error uploading one of the files. Please try again.");

              setItems((prev) =>
                prev.filter((it) => it.preview !== item.preview),
              );

              URL.revokeObjectURL(item.preview);
            });
        });

        return [...current, ...newItems];
      });
    },
    [allowedTypes, allowedTypePrefix, maxSizeMB, maxCount, isMultiple],
  );

  return {
    items,
    error,
    addFiles,
    removeAt,
    reset,
    setError,
  };
}
